import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, Music, Youtube } from "lucide-react";

function spotifyEmbed(r) {
  return `https://open.spotify.com/embed/${r.kind}/${r.spotify_id}?utm_source=jamesone&theme=0`;
}
function spotifyOpen(r) {
  return `https://open.spotify.com/${r.kind}/${r.spotify_id}`;
}
function youtubeEmbed(r) {
  return `https://www.youtube.com/embed/videoseries?list=${r.youtube_playlist_id}&modestbranding=1&rel=0`;
}
function youtubeOpen(r) {
  return `https://www.youtube.com/playlist?list=${r.youtube_playlist_id}`;
}

export default function MusicSection() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/releases").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  if (!items.length) return null;
  const spotifyItem = items.find((i) => i.kind === "artist" || i.kind === "playlist" || i.kind === "track") || items[0];
  const youtubeItem = items.find((i) => i.kind === "youtube");

  return (
    <section id="music" className="py-24 md:py-36 bg-warmwhite border-t border-taupe/30" data-testid="music-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="text-[11px] uppercase tracking-widest-custom text-olive mb-6">04 — Music</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-3xl" data-testid="music-title">
              The sound of <span className="italic text-gold">James 1:1.</span>
            </h2>
          </div>
          {spotifyItem && (
            <a
              href={spotifyOpen(spotifyItem)}
              target="_blank"
              rel="noreferrer"
              data-testid="music-open-spotify"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest-custom text-charcoal hover:text-gold transition-colors"
            >
              Open on Spotify <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {spotifyItem && (
            <div className="lg:col-span-7" data-testid="music-featured">
              <div className="aspect-square md:aspect-[5/4] lg:aspect-[16/12] w-full overflow-hidden border border-taupe/40 bg-charcoal/5">
                <iframe
                  title={spotifyItem.title}
                  src={spotifyEmbed(spotifyItem)}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                />
              </div>
              <div className="mt-5 flex items-baseline gap-3">
                <Music className="w-4 h-4 text-gold mt-1" strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-2xl md:text-3xl">{spotifyItem.title}</p>
                  <p className="text-xs uppercase tracking-widest-custom text-charcoal/55 mt-1">Featured · Spotify {spotifyItem.kind}</p>
                </div>
              </div>
            </div>
          )}

          {youtubeItem && (
            <div className="lg:col-span-5" data-testid="music-youtube">
              <div className="aspect-video w-full overflow-hidden border border-taupe/40 bg-charcoal/5">
                <iframe
                  title={youtubeItem.title}
                  src={youtubeEmbed(youtubeItem)}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="mt-5 flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-3">
                  <Youtube className="w-4 h-4 text-gold mt-1" strokeWidth={1.5} />
                  <div>
                    <p className="font-serif text-2xl">{youtubeItem.title}</p>
                    <p className="text-xs uppercase tracking-widest-custom text-charcoal/55 mt-1">YouTube playlist</p>
                  </div>
                </div>
                <a
                  href={youtubeOpen(youtubeItem)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="music-open-youtube"
                  className="text-[11px] uppercase tracking-widest-custom text-charcoal hover:text-gold transition-colors inline-flex items-center gap-1.5 shrink-0"
                >
                  Watch <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
